/*
  DogPure · ficha de producto.
  <dp-gallery>: galería manual (deslizar, flechas, miniaturas) y ampliación en <dialog>. Sin avance automático.
  <dp-share>: compartir con el menú del sistema o copiar el enlace.
  <dp-sticky-buy>: barra inferior que aparece cuando el botón principal sale de pantalla por arriba;
    pulsa ese mismo botón, así respeta cantidad, variante y disponibilidad del formulario real.
  <dp-variant-select>: solo si el producto tiene varias variantes; actualiza id, precio y disponibilidad.
*/

if (!customElements.get('dp-gallery')) {
  customElements.define(
    'dp-gallery',
    class DpGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.thumbs = [...this.querySelectorAll('[data-thumb]')];
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.dialog = this.querySelector('dialog');
        this.index = 0;
        if (!this.track || this.slides.length === 0) return;

        this.prev?.addEventListener('click', () => this.go(this.index - 1));
        this.next?.addEventListener('click', () => this.go(this.index + 1));
        this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
        this.track.addEventListener('scroll', () => {
          window.cancelAnimationFrame(this.raf);
          this.raf = window.requestAnimationFrame(() => {
            const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
            if (i !== this.index) this.setIndex(i);
          });
        }, { passive: true });
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.go(this.index + 1);
          if (event.key === 'ArrowLeft') this.go(this.index - 1);
        });

        // Ampliación
        this.querySelector('[data-zoom]')?.addEventListener('click', () => this.openZoom());
        if (this.dialog) {
          this.dialogImg = this.dialog.querySelector('img');
          this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
          this.dialog.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.zoomTo(this.index - 1));
          this.dialog.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.zoomTo(this.index + 1));
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });
          this.dialog.addEventListener('close', () => this.querySelector('[data-zoom]')?.focus());
        }
        this.setIndex(0);
      }

      go(i) {
        const target = Math.max(0, Math.min(this.slides.length - 1, i));
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
        this.setIndex(target);
      }

      setIndex(i) {
        this.index = i;
        this.thumbs.forEach((thumb, n) => thumb.setAttribute('aria-current', String(n === i)));
        this.slides.forEach((slide, n) => slide.setAttribute('aria-hidden', String(n !== i)));
        if (this.prev) this.prev.disabled = i === 0;
        if (this.next) this.next.disabled = i === this.slides.length - 1;
        const status = this.querySelector('[data-status]');
        if (status) status.textContent = `${i + 1} / ${this.slides.length}`;
        const count = this.querySelector('[data-count]');
        if (count) count.textContent = `${i + 1} / ${this.slides.length}`;
      }

      openZoom() {
        if (!this.dialog) return;
        this.zoomTo(this.index);
        this.dialog.showModal();
      }

      zoomTo(i) {
        const target = Math.max(0, Math.min(this.slides.length - 1, i));
        const slide = this.slides[target];
        this.dialogImg.src = slide.dataset.zoomSrc;
        this.dialogImg.alt = slide.dataset.alt || '';
        this.dialog.querySelector('[data-zoom-prev]').disabled = target === 0;
        this.dialog.querySelector('[data-zoom-next]').disabled = target === this.slides.length - 1;
        if (target !== this.index) this.go(target);
      }
    }
  );
}

if (!customElements.get('dp-share')) {
  customElements.define(
    'dp-share',
    class DpShare extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.status = this.querySelector('[data-status]');
        this.button?.addEventListener('click', () => this.share());
      }

      async share() {
        const url = this.dataset.url || window.location.href;
        if (navigator.share) {
          try {
            await navigator.share({ title: this.dataset.title, url });
          } catch (error) {
            /* El visitante cerró el menú de compartir */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          this.status.textContent = this.dataset.copied;
        } catch (error) {
          this.status.textContent = url;
        }
      }
    }
  );
}

if (!customElements.get('dp-sticky-buy')) {
  customElements.define(
    'dp-sticky-buy',
    class DpStickyBuy extends HTMLElement {
      connectedCallback() {
        this.source = document.getElementById(this.dataset.source);
        this.button = this.querySelector('button');
        if (!this.source || !this.button) return;

        this.button.addEventListener('click', () => {
          this.source.click();
        });

        this.observer = new IntersectionObserver(([entry]) => {
          // Solo cuando el botón principal quedó por encima de la pantalla
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggleAttribute('data-visible', passed);
          this.setAttribute('aria-hidden', String(!passed));
          this.button.tabIndex = passed ? 0 : -1;
          // Reserva su altura al final de la página para no tapar el pie
          const shown = passed && getComputedStyle(this).display !== 'none';
          document.body.style.paddingBottom = shown ? `${this.offsetHeight}px` : '';
        });
        this.observer.observe(this.source);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.body.style.paddingBottom = '';
      }
    }
  );
}

if (!customElements.get('dp-variant-select')) {
  customElements.define(
    'dp-variant-select',
    class DpVariantSelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        this.section = this.closest('[data-dp-product]');
        this.select?.addEventListener('change', () => this.update());
      }

      update() {
        const option = this.select.selectedOptions[0];
        const form = this.section.querySelector('form[data-type="add-to-cart-form"]');
        form.querySelector('input[name="id"]').value = option.value;
        this.section.querySelectorAll('[data-price]').forEach((el) => (el.textContent = option.dataset.price));
        const available = option.dataset.available === 'true';
        this.section.querySelectorAll('[data-buy-button]').forEach((button) => {
          button.toggleAttribute('disabled', !available);
          const label = button.querySelector('[data-label]');
          if (label) label.textContent = available ? button.dataset.addLabel : button.dataset.soldOutLabel;
        });
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    }
  );
}
